import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import SearchTable from './SearchTable/SearchTable';
import CSVExport from '../CSVExport';
import { PageFilters } from '../PageFilters';
import { formatPhoneNumber } from '../../../utils/number-util';
import type { Props } from './types';

const InlineEditTable = (props: Props): JSX.Element => {
  const {
    api,
    filters,
    constantFilters,
    filterConfigs = [],
    maxChicletFilters = 4,
    filterValues,
    columns = [],
    childColumns,
    rows,
    headerActions,
    filterFooterContent,
    rowKey,
    onRowClick,
    rowClickInfo,
    highlightedRow,
    onRowUpdate,
    onCellCommit,
    searchInfo,
    className,
    wrapperClassName,
    tableClassName,
    emptyMessage = null,
    isLoading,
    renderEmpty,
    searchData: searchDataProp,
    onSort,
    sortState,
    saveOnChange,
    paginationInfo,
    paginateLocally,
    rowClassName,
    entryRow,
  } = props;

  const [selectedFilterValues, setSelectedFilterValues] = useState<Record<string, any>>({});

  useEffect(() => {
    if (filterValues) return;
    const defaults: Record<string, any> = { ...(constantFilters || {}) };
    if (filters && filters.length > 0) {
      filters.forEach((filter) => {
        if (filter.defaultValue !== undefined) {
          defaults[filter.keyName] = filter.defaultValue;
        }
      });
    }
    setSelectedFilterValues(defaults);
  }, [filters, constantFilters, filterValues]);

  const tableColumns = useMemo(() => columns, [columns]);
  const effectiveFilterValues = filterValues || selectedFilterValues;
  const exportHeaders = useMemo(
    () =>
      columns.map((header) => ({
        name: header.title,
        key: header.keyName ?? '',
        databaseProperty: '',
      })),
    [columns]
  );
  const filteredRows = useMemo(() => {
    if (!rows || !filters || filters.length === 0) return rows;
    return rows.filter((row) => {
      return filters.every((filter) => {
        const selected = effectiveFilterValues[filter.keyName];
        if (selected === undefined || selected === null || selected === '' || selected === 'All') {
          return true;
        }
        const rowValue = (row as Record<string, any>)[filter.keyName];

        if (filter.type === 'search') {
          return String(rowValue || '')
            .toLowerCase()
            .includes(String(selected).toLowerCase());
        }

        if (filter.type === 'select') {
          if (Array.isArray(selected)) {
            if (selected.length === 0) return true;
            return selected.map(String).includes(String(rowValue ?? ''));
          }
          return String(rowValue ?? '') === String(selected);
        }

        if (filter.type === 'date') {
          if (!rowValue) return false;
          const rowDate = moment(rowValue, ['MM/DD/YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
          const selectedDate = moment(selected);
          if (!rowDate.isValid() || !selectedDate.isValid()) return false;
          return rowDate.startOf('day').isSame(selectedDate.startOf('day'), 'day');
        }

        if (filter.type === 'number') {
          if (rowValue === undefined || rowValue === null || rowValue === '') return false;
          return Number(rowValue) === Number(selected);
        }

        return true;
      });
    });
  }, [rows, filters, effectiveFilterValues]);
  const exportData = useMemo(
    () =>
      (filteredRows || []).map((data) => ({
        ...data,
        customerPhone: formatPhoneNumber(data.customerPhone),
        _costRaw: new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: data?._costCurrency ?? 'USD',
        }).format(data?.cost),
      })),
    [filteredRows]
  );

  return (
    <div className={className || 'inline-edit-table'}>
      {filterConfigs.length > 0 && (
        <div style={{ marginBottom: '0.75rem' }}>
          <div className="filters-actions-container mb-3">
            <PageFilters filters={filterConfigs} maxChicletFilters={maxChicletFilters} />
            <div className="page-actions">
              {headerActions}
              <CSVExport data={exportData} headers={exportHeaders} noMargin={true} />
            </div>
          </div>
          {filterFooterContent}
        </div>
      )}
      <SearchTable
        columns={tableColumns}
        childColumns={childColumns}
        filters={filters}
        searchData={searchDataProp}
        editableColumns={columns.filter((col) => col.editable).map((col) => col.keyName || '')}
        selectedFilterValues={selectedFilterValues}
        api={api}
        rows={filteredRows}
        paginationInfo={paginationInfo}
        paginateLocally={paginateLocally}
        rowKey={rowKey}
        onRowClick={onRowClick}
        rowClickInfo={rowClickInfo}
        highlightedRow={highlightedRow}
        onRowUpdate={onRowUpdate}
        onCellCommit={onCellCommit}
        searchInfo={searchInfo}
        wrapperClassName={wrapperClassName}
        tableClassName={tableClassName}
        emptyMessage={emptyMessage}
        isLoading={isLoading}
        renderEmpty={renderEmpty}
        headerActions={filterConfigs.length > 0 ? null : headerActions}
        onSort={onSort}
        sortState={sortState}
        saveOnChange={saveOnChange}
        rowClassName={rowClassName}
        entryRow={entryRow}
      />
    </div>
  );
};

export default InlineEditTable;
